// Client for the domain_manager domain-management API (admin UI).
// Calls go to sm4rt-api's staff-authenticated passthrough (/api/domain-mgr/* ->
// domain_manager /api/*), which adds the function key server-side so the browser
// never holds it. Dev: the CRA '/api' proxy sends this to sm4rt-api on :7071.

const API_BASE_URL = '/api/domain-mgr';

export interface DomainInvoiceRef {
  number: string | null;
  rsId: string | null; // RepairShopr invoice id
  date: string | null;
  isPaid: boolean;
}

export interface DomainListItem {
  id: string; // cr001_domainsid
  name: string;
  status: string | null;
  expiry: string | null;
  autoRenew: boolean;
  registrantEmail: string | null;
  actionOnExpiry: string | null;
  techsolversOwned: boolean;
  createdDate: string | null;
  businessName: string | null;
  customerId: string | null;
  /** Dataverse GUID of the current owning customer (null when unassigned). */
  ownerCustomerGuid: string | null;
  hasOwner: boolean;
  invoice: DomainInvoiceRef | null;
  lastPaidDate: string | null;
}

export type DomainDetail = DomainListItem;

export interface DomainUpdate {
  autoRenew?: boolean;
  actionOnExpiry?: string;
  /** Dataverse customer GUID to assign as the domain's owner (points the domain
   *  row's cr001_customer_id lookup at them — the ownership source of truth). */
  ownerCustomerGuid?: string;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`Request failed (${response.status}): ${text}`);
  }
  return response.json();
}

export async function fetchDomains(signal?: AbortSignal): Promise<DomainListItem[]> {
  const response = await fetch(`${API_BASE_URL}/domains`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  const data = await handleResponse<{ domains: DomainListItem[] }>(response);
  return data.domains;
}

export async function fetchDomainDetail(id: string, signal?: AbortSignal): Promise<DomainDetail> {
  const response = await fetch(`${API_BASE_URL}/domains/${encodeURIComponent(id)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  const data = await handleResponse<{ domain: DomainDetail }>(response);
  return data.domain;
}

export async function updateDomain(
  id: string,
  update: DomainUpdate,
  signal?: AbortSignal,
): Promise<DomainDetail> {
  const response = await fetch(`${API_BASE_URL}/domains/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(update),
    signal,
  });
  const data = await handleResponse<{ domain: DomainDetail }>(response);
  return data.domain;
}
